/**
 * Form Submit Controller
 *
 * A generic utility controller that provides programmatic form submission.
 * Used across the application for search forms, filter components, and auto-submit scenarios.
 *
 * Usage:
 *   <form data-controller="form-submit">
 *     <select data-action="change->form-submit#submit">...</select>
 *   </form>
 *
 * Architecture Decision: NO BREADCRUMBS
 * =====================================
 * This controller is intentionally kept SIMPLE and GENERIC for the following reasons:
 *
 * 1. **Too Generic**: Used in 30+ places across the app with different contexts
 *    - Publication search filters (year, language, category, author, etc.)
 *    - Admin search forms (users, journals, authorities, categories, etc.)
 *    - Sort dropdowns
 *    - Collection search filters
 *
 * 2. **Context Varies**: Each usage has different semantic meaning
 *    - Filtering by year is different from sorting by title
 *    - Searching users is different from searching publications
 *    - Generic breadcrumb here would add noise, not signal
 *
 * 3. **Responsibility**: Form submissions ARE user actions, but breadcrumb
 *    tracking should happen at the CONTEXT level, not in this generic helper:
 *    - Publication search filters → tracked by filter component controllers
 *    - Sort changes → tracked by sort_button_component
 *    - Search forms → tracked by search controllers
 *
 * 4. **Keep It Simple**: This is a reusable utility, like a helper method.
 *    Adding breadcrumbs here would be like adding breadcrumbs to every
 *    Rails form_with() helper - not meaningful.
 *
 * If you need to track form submissions from this controller, add breadcrumbs
 * in the CALLING context (the component or controller that triggers the action),
 * not in this generic helper.
 *
 * @extends ApplicationController
 */

import ApplicationController from './application_controller'

export default class extends ApplicationController {
  /**
   * Programmatically submits the closest parent form.
   * Uses requestSubmit() to ensure form validation and submit events fire.
   */
  submit() {
    this.element.closest('form').requestSubmit()
  }
}
